import React, { useEffect, useMemo, useState } from "react";
import { Card, Avatar, Tag, Empty, Progress, Statistic, Row, Col, Typography, Tooltip, Spin, Modal, DatePicker, Table, Button, Dropdown, Switch } from "antd";
import { TrophyOutlined, InfoCircleOutlined, DownOutlined, RightOutlined, MoreOutlined, FieldTimeOutlined, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../../core/services/axiosConfig";
import { apis } from "../../../core/services/params";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
    ResponsiveContainer, Cell, PieChart, Pie, Legend,
    AreaChart, Area, ReferenceLine,
} from "recharts";
import moment from "moment";
import AgentProfileCharts from "./AgentProfileCharts";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    PhoneOutlined,
    ClockCircleOutlined,
    RobotOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const AGENT_COLORS = [
    "#1677ff", "#52c41a", "#fa8c16", "#f5222d",
    "#722ed1", "#13c2c2", "#eb2f96", "#fadb14",
];

const rateColor = (pct: number) =>
    pct >= 80 ? "#52c41a" : pct >= 50 ? "#faad14" : "#ff4d4f";

interface TimeBucket {
    bucket: string;
    calls: number;
    status_breakdown: Record<string, number>;
}

interface DurationBucket {
    key: string;
    label: string;
    calls: number;
    total_seconds: number;
    total_minutes: number;
    billed_minutes: number;
}

interface AgentRow {
    agent: string;
    status_breakdown: Record<string, number>;
    total_calls: number;
    total_duration: string;
    total_duration_seconds: number;
    time_series?: TimeBucket[];
    bucket_interval?: string;
    csat_out_of_5?: number;
    csat_responses?: number;
    csat_distribution?: {
        No_input?: number;
        Not_Satisfied?: number;
        Satisfied?: number;
        [key: string]: number | undefined;
    };
    feedback_distribution?: {
        Good?: number;
        Average?: number;
        Poor?: number;
        [key: string]: number | undefined;
    };
    duration_buckets?: DurationBucket[];
    total_billed_minutes?: number;
    zero_duration_calls?: number;
}

interface DimensionScore {
    calls_scored: number;
    calls_skipped: number;
    score: number | null;
    weight: string;
}

interface AgentProfile {
    agent: string;
    overall_score: number;
    grade: string;
    label: string;
    total_calls_analyzed: number;
    dimension_scores: Record<string, DimensionScore>;
    metadata: {
        priority_distribution: Record<string, number>;
        resolution_distribution: Record<string, number>;
        scoring_note: string;
    };
}

interface Props {
    agentWiseReport: AgentRow[];
    loading?: boolean;
    agentProfiles?: AgentProfile[];
    loadingProfiles?: boolean;
    saharaTicketsCount?: number | null;
    dateRange?: [string, string];
    callType?: string;
}

// @@ AI-COMMENT: Custom bar label showing value above each bar
const BarLabel = ({ x, y, width, value }: any) => {
    if (!value) return null;
    return (
        <text x={x + width / 2} y={y - 4} fill="#595959" textAnchor="middle" fontSize={11}>
            {value}
        </text>
    );
};

// @@ AI-COMMENT: Return bucket label exactly as received from the API
const shortBucket = (bucket: string): string => bucket;


const DIMENSION_LABELS: Record<string, string> = {
    professionalism: "Professionalism",
    issue_resolution: "Issue Resolution",
    customer_satisfaction: "Customer Satisfaction",
    call_closure_quality: "Call Closure",
    empathy_soft_skills: "Empathy",
    knowledge_competency: "Knowledge",
};

interface CsatFunnelRow {
    agent: string;
    call_ended: number;
    feedback_requested: number;
    request_failed: number;
    feedback_received: number;
    good: number;
    average: number;
    poor: number;
    unrecognised: number;
    csat_out_of_5: number | null;
    scored_responses: number;
    response_rate: number;
}

const FunnelStep = ({ label, value, of, hint }: { label: string; value: number; of?: number; hint?: string }) => {
    const pct = of && of > 0 ? Math.round((value / of) * 100) : null;
    // @@ AI-COMMENT: every percentage in this funnel is a share of calls handled
    return (
        <div className="flex-1" style={{ minWidth: 150 }}>
            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>{label}</Text>
            <div className="flex items-baseline gap-2">
                <span style={{ fontSize: 22, fontWeight: 600 }}>{value}</span>
                {pct !== null && <Text type="secondary" style={{ fontSize: 12 }}>{pct}% of calls</Text>}
            </div>
            {hint && <Text type="secondary" style={{ fontSize: 10 }}>{hint}</Text>}
        </div>
    );
};

const CsatDetailsModal = ({
    open,
    onClose,
    defaultRange,
    agent,
}: {
    open: boolean;
    onClose: () => void;
    defaultRange?: [string, string];
    agent?: string | null;
}) => {
    const [range, setRange] = useState<any[]>([]);
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // @@ AI-COMMENT: Seed the picker from the dashboard's range each time it opens
    useEffect(() => {
        if (!open) return;
        setRange([
            defaultRange?.[0] ? dayjs(defaultRange[0]) : dayjs().startOf("day"),
            defaultRange?.[1] ? dayjs(defaultRange[1]) : dayjs(),
        ]);
    }, [open, defaultRange?.[0], defaultRange?.[1]]);

    useEffect(() => {
        if (!open || !range[0] || !range[1]) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        axiosInstance
            .get(
                `${apis.OZONETEL_CSAT_DETAILS}?start_date=${range[0].format("YYYY-MM-DD")}&end_date=${range[1].format("YYYY-MM-DD")}`
            )
            .then((res: any) => { if (!cancelled) setData(res.data?.data ?? null); })
            .catch((err: any) => {
                if (cancelled) return;
                console.error("Failed to fetch CSAT details", err);
                setError("Could not load CSAT details.");
                setData(null);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, range]);

    // @@ AI-COMMENT: Opened from one agent's card, so report that agent's row
    // rather than the all-voice-agent totals.
    const allRows: CsatFunnelRow[] = data?.agents ?? [];
    const agentRows: CsatFunnelRow[] = agent
        ? allRows.filter(r => r.agent?.toUpperCase() === agent.toUpperCase())
        : allRows;
    const t: CsatFunnelRow | null = agent
        ? agentRows[0] ?? null
        : data?.totals ?? null;
    const noPrompt = t ? Math.max(t.call_ended - t.feedback_requested - t.request_failed, 0) : 0;
    const awaitingReply = t ? Math.max(t.feedback_requested - t.feedback_received, 0) : 0;

    const columns = [
        { title: "Agent", dataIndex: "agent", key: "agent", fixed: "left" as const, width: 150 },
        { title: "Calls", dataIndex: "call_ended", key: "call_ended", width: 80 },
        { title: "Prompted", dataIndex: "feedback_requested", key: "feedback_requested", width: 95 },
        { title: "Replied", dataIndex: "feedback_received", key: "feedback_received", width: 85 },
        {
            title: "Reply rate", dataIndex: "response_rate", key: "response_rate", width: 100,
            render: (v: number) => `${v}%`,
        },
        { title: "Good", dataIndex: "good", key: "good", width: 75 },
        { title: "Average", dataIndex: "average", key: "average", width: 90 },
        { title: "Poor", dataIndex: "poor", key: "poor", width: 75 },
        {
            title: "CSAT", dataIndex: "csat_out_of_5", key: "csat_out_of_5", width: 110,
            render: (v: number | null, row: CsatFunnelRow) =>
                v === null
                    ? <Text type="secondary">—</Text>
                    : <span className="text-yellow-600 font-semibold">
                        ⭐ {v.toFixed(2)}{" "}
                        <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
                            (n={row.scored_responses})
                        </Text>
                    </span>,
        },
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={980}
            title={
                <div className="flex items-center gap-2">
                    <span>Call Feedback{agent ? ` — ${agent}` : ""}</span>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                        How customers rated their call on WhatsApp
                    </Text>
                </div>
            }
        >
            {/* @@ AI-COMMENT: Spell out the real-world flow — most viewers have never
                seen the WhatsApp prompt and will not know where these numbers come from */}
            <div
                className="mb-4 flex flex-wrap items-center gap-2"
                style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8, padding: "10px 14px" }}
            >
                <Tag color="default" style={{ margin: 0 }}>1. Call ends</Tag>
                <Text type="secondary">→</Text>
                <Tag color="blue" style={{ margin: 0 }}>2. We WhatsApp the customer “How was the call?”</Tag>
                <Text type="secondary">→</Text>
                <Tag color="green" style={{ margin: 0 }}>3. They tap Good, Average or Poor</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Those replies are the score — a call with no reply is not counted.
                </Text>
            </div>

            <div className="mb-4">
                <DatePicker.RangePicker
                    value={range as any}
                    onChange={vals => { if (vals && vals[0] && vals[1]) setRange(vals as any); }}
                    allowClear={false}
                    format="DD MMM YYYY"
                />
            </div>

            {loading ? (
                <div className="flex justify-center py-10"><Spin /></div>
            ) : error ? (
                <Empty description={error} />
            ) : !t || t.call_ended === 0 ? (
                <Empty description={`No ${agent ?? "voice agent"} calls in this date range`} />
            ) : (
                <>
                    <Card bordered={false} className="shadow-sm mb-4" bodyStyle={{ padding: 16 }}>
                        <div className="flex flex-wrap gap-4">
                            <FunnelStep label="Calls handled" value={t.call_ended}
                                hint="calls that ended in this period" />
                            <FunnelStep label="Asked on WhatsApp" value={t.feedback_requested} of={t.call_ended}
                                hint="“How was the call?” delivered" />
                            <FunnelStep label="Customers replied" value={t.feedback_received} of={t.call_ended}
                                hint="tapped Good / Average / Poor" />
                            <div className="flex-1" style={{ minWidth: 150 }}>
                                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Satisfaction score</Text>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-yellow-600" style={{ fontSize: 22, fontWeight: 600 }}>
                                        {t.csat_out_of_5 === null ? "—" : `⭐ ${t.csat_out_of_5.toFixed(2)} / 5`}
                                    </span>
                                </div>
                                <Text type="secondary" style={{ fontSize: 10 }}>
                                    average of {t.scored_responses} {t.scored_responses === 1 ? "reply" : "replies"}
                                </Text>
                            </div>
                        </div>
                    </Card>

                    <Row gutter={[16, 16]} className="mb-4">
                        <Col xs={24} md={12}>
                            <Card bordered={false} className="shadow-sm h-full" bodyStyle={{ padding: 16 }}
                                title={<span style={{ fontSize: 13 }}>Why the other calls have no rating</span>}>
                                <div className="flex justify-between text-sm mb-3">
                                    <div>
                                        <div>No WhatsApp was sent</div>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            call never reached the feedback step
                                        </Text>
                                    </div>
                                    <b>{noPrompt}</b>
                                </div>
                                <div className="flex justify-between text-sm mb-3">
                                    <div>
                                        <div>WhatsApp failed to send</div>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            message could not be delivered
                                        </Text>
                                    </div>
                                    <b>{t.request_failed}</b>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <div>
                                        <div>Sent, but no reply</div>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            customer has not answered yet
                                        </Text>
                                    </div>
                                    <b>{awaitingReply}</b>
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card bordered={false} className="shadow-sm h-full" bodyStyle={{ padding: 16 }}
                                title={<span style={{ fontSize: 13 }}>What customers replied</span>}>
                                {[
                                    { label: "Good", value: t.good, score: 5, color: "#52c41a" },
                                    { label: "Average", value: t.average, score: 2.5, color: "#faad14" },
                                    { label: "Poor", value: t.poor, score: 0, color: "#ff4d4f" },
                                ].map(r => (
                                    <div key={r.label} className="mb-2">
                                        <div className="flex justify-between text-sm">
                                            <span>{r.label} <Text type="secondary" style={{ fontSize: 11 }}>counts as {r.score} / 5</Text></span>
                                            <b>{r.value}</b>
                                        </div>
                                        <Progress
                                            percent={t.scored_responses > 0 ? Math.round((r.value / t.scored_responses) * 100) : 0}
                                            strokeColor={r.color} trailColor="#f5f5f5" size="small" strokeWidth={6}
                                        />
                                    </div>
                                ))}
                                {t.unrecognised > 0 && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        {t.unrecognised} {t.unrecognised === 1 ? "reply was" : "replies were"} not Good/Average/Poor and {t.unrecognised === 1 ? "is" : "are"} left out of the score
                                    </Text>
                                )}
                            </Card>
                        </Col>
                    </Row>

                    {!agent && <Table
                        dataSource={agentRows}
                        columns={columns}
                        rowKey="agent"
                        size="small"
                        pagination={false}
                        scroll={{ x: 900 }}
                    />}
                </>
            )}
        </Modal>
    );
};

const DURATION_BUCKET_LABELS: Record<string, string> = {
    under_30s: "< 30 sec",
    "30s_1m": "30 sec – 1 min",
    "1m_2m": "1 min – 2 min",
    above_2m: "> 2 min",
};

const BUCKET_COLORS: Record<string, string> = {
    under_30s: "#ff4d4f",
    "30s_1m": "#faad14",
    "1m_2m": "#1677ff",
    above_2m: "#52c41a",
};

const fmtHMS = (sec: number) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.round(sec % 60);
    return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
};

const CallDurationModal = ({
    open,
    onClose,
    agent,
    defaultRange,
    callType = "InBound",
}: {
    open: boolean;
    onClose: () => void;
    agent: (AgentRow & { color?: string; answered?: number; notAnswered?: number }) | null;
    defaultRange?: [string, string];
    callType?: string;
}) => {
    const [range, setRange] = useState<any[]>([]);
    const [row, setRow] = useState<AgentRow | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const agentName = agent?.agent ?? null;

    useEffect(() => {
        if (!open) return;
        setRange([
            defaultRange?.[0] ? dayjs(defaultRange[0]) : dayjs().startOf("day"),
            defaultRange?.[1] ? dayjs(defaultRange[1]) : dayjs(),
        ]);
    }, [open, defaultRange?.[0], defaultRange?.[1]]);

    useEffect(() => {
        if (!open || !agentName || !range[0] || !range[1]) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        axiosInstance
            .post(`${apis.OZONETEL_LIVE_REPORT}?page=1&per_page=1`, {
                filters: {
                    start_date: range[0].startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    end_date: range[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    Type: callType,
                },
            })
            .then((res: any) => {
                if (cancelled) return;
                const rows: AgentRow[] = res.data?.agent_wise_report ?? [];
                setRow(
                    rows.find(r => r.agent?.toUpperCase() === agentName.toUpperCase()) ?? null
                );
            })
            .catch((err: any) => {
                if (cancelled) return;
                console.error("Failed to fetch call duration breakdown", err);
                setError("Could not load call duration breakdown.");
                setRow(null);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, agentName, callType, range]);

    const buckets: DurationBucket[] = (row?.duration_buckets ?? []).map(b => ({
        ...b,
        label: DURATION_BUCKET_LABELS[b.key] ?? b.label,
    }));

    const totalCalls = buckets.reduce((acc, b) => acc + b.calls, 0);
    const totalSeconds = buckets.reduce((acc, b) => acc + b.total_seconds, 0);
    const totalMinutes = totalSeconds / 60;
    const totalBilled = buckets.reduce((acc, b) => acc + b.billed_minutes, 0);
    const zeroDuration = row?.zero_duration_calls ?? 0;

    const columns = [
        {
            title: "Call length",
            dataIndex: "label",
            key: "label",
            render: (label: string, row: DurationBucket) => (
                <span className="flex items-center gap-2">
                    <span style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: BUCKET_COLORS[row.key] ?? "#8c8c8c", display: "inline-block",
                    }} />
                    {label}
                </span>
            ),
        },
        {
            title: "Calls",
            dataIndex: "calls",
            key: "calls",
            width: 90,
            render: (v: number) => <b>{v}</b>,
        },
        {
            title: "% of calls",
            key: "callShare",
            width: 170,
            render: (_: any, row: DurationBucket) => {
                const pct = totalCalls > 0 ? Math.round((row.calls / totalCalls) * 100) : 0;
                return (
                    <div style={{ minWidth: 130 }}>
                        <Progress
                            percent={pct}
                            size="small"
                            strokeWidth={6}
                            strokeColor={BUCKET_COLORS[row.key] ?? "#1677ff"}
                            trailColor="#f5f5f5"
                            format={p => <span style={{ fontSize: 11 }}>{p ?? 0}%</span>}
                        />
                    </div>
                );
            },
        },
        {
            title: <Tooltip title="Real talk time on the call">Actual min</Tooltip>,
            dataIndex: "total_minutes",
            key: "total_minutes",
            width: 120,
            render: (v: number, row: DurationBucket) => (
                <Tooltip title={fmtHMS(row.total_seconds)}>
                    <b>{v.toFixed(1)}</b> <Text type="secondary" style={{ fontSize: 11 }}>min</Text>
                </Tooltip>
            ),
        },
        {
            title: <Tooltip title="Each call rounded up to the next whole minute">Billed min</Tooltip>,
            dataIndex: "billed_minutes",
            key: "billed_minutes",
            width: 130,
            render: (v: number, row: DurationBucket) => {
                const extra = v - row.total_minutes;
                return (
                    <Tooltip title={`${row.calls} calls rounded up — ${extra.toFixed(1)} min added`}>
                        <b style={{ color: "#1677ff" }}>{v}</b>{" "}
                        <Text type="secondary" style={{ fontSize: 11 }}>min</Text>
                        {extra > 0 && (
                            <Text type="secondary" style={{ fontSize: 10, marginLeft: 4 }}>
                                +{extra.toFixed(1)}
                            </Text>
                        )}
                    </Tooltip>
                );
            },
        },
        {
            title: "% of billed min",
            key: "minuteShare",
            width: 170,
            render: (_: any, row: DurationBucket) => {
                const pct = totalBilled > 0 ? Math.round((row.billed_minutes / totalBilled) * 100) : 0;
                return (
                    <div style={{ minWidth: 130 }}>
                        <Progress
                            percent={pct}
                            size="small"
                            strokeWidth={6}
                            strokeColor={BUCKET_COLORS[row.key] ?? "#1677ff"}
                            trailColor="#f5f5f5"
                            format={p => <span style={{ fontSize: 11 }}>{p ?? 0}%</span>}
                        />
                    </div>
                );
            },
        },
        {
            title: "Avg / call",
            key: "avg",
            width: 110,
            render: (_: any, row: DurationBucket) =>
                row.calls > 0
                    ? <Text type="secondary">{fmtHMS(Math.round(row.total_seconds / row.calls))}</Text>
                    : <Text type="secondary">—</Text>,
        },
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1040}
            title={
                <div className="flex items-center gap-2">
                    <FieldTimeOutlined />
                    <span>Call Duration Breakdown{agent ? ` — ${agent.agent}` : ""}</span>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                        Actual talk time vs billed minutes, split by how long the call ran
                    </Text>
                </div>
            }
        >
            <div className="mb-4">
                <DatePicker.RangePicker
                    value={range as any}
                    onChange={vals => { if (vals && vals[0] && vals[1]) setRange(vals as any); }}
                    allowClear={false}
                    format="DD MMM YYYY"
                />
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 10 }}>
                    {callType} calls
                </Text>
            </div>

            {loading ? (
                <div className="flex justify-center py-10"><Spin /></div>
            ) : error ? (
                <Empty description={error} />
            ) : !row || totalCalls === 0 ? (
                <Empty description={`No ${agent?.agent ?? "agent"} calls with talk time in this date range`} />
            ) : (
                <>
                    <Card bordered={false} className="shadow-sm mb-4" bodyStyle={{ padding: 16 }}>
                        <div className="flex flex-wrap gap-4">
                            <div className="flex-1" style={{ minWidth: 150 }}>
                                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Calls with talk time</Text>
                                <span style={{ fontSize: 22, fontWeight: 600 }}>{totalCalls}</span>
                                <Text type="secondary" style={{ fontSize: 10, display: "block" }}>
                                    out of {row.total_calls} total calls
                                </Text>
                            </div>
                            <div className="flex-1" style={{ minWidth: 150 }}>
                                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Actual talk time</Text>
                                <span style={{ fontSize: 22, fontWeight: 600 }}>{totalMinutes.toFixed(1)}</span>
                                <Text type="secondary" style={{ fontSize: 13 }}> min</Text>
                                <Text type="secondary" style={{ fontSize: 10, display: "block" }}>
                                    {fmtHMS(totalSeconds)}
                                </Text>
                            </div>
                            <div className="flex-1" style={{ minWidth: 150 }}>
                                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Billed minutes</Text>
                                <span style={{ fontSize: 22, fontWeight: 600, color: "#1677ff" }}>{totalBilled}</span>
                                <Text type="secondary" style={{ fontSize: 13 }}> min</Text>
                                <Text type="secondary" style={{ fontSize: 10, display: "block" }}>
                                    +{(totalBilled - totalMinutes).toFixed(1)} min from rounding up
                                </Text>
                            </div>
                            <div className="flex-1" style={{ minWidth: 150 }}>
                                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Avg call length</Text>
                                <span style={{ fontSize: 22, fontWeight: 600 }}>
                                    {fmtHMS(Math.round(totalSeconds / totalCalls))}
                                </span>
                            </div>
                            <div className="flex-1" style={{ minWidth: 150 }}>
                                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>No talk time</Text>
                                <span style={{ fontSize: 22, fontWeight: 600 }}>{zeroDuration}</span>
                                <Text type="secondary" style={{ fontSize: 10, display: "block" }}>
                                    missed / 0-second calls, not bucketed
                                </Text>
                            </div>
                        </div>
                    </Card>

                    {/* @@ AI-COMMENT: One stacked bar makes the mix readable at a glance
                        before the reader drops into the per-bucket numbers */}
                    <div className="mb-2">
                        <Text type="secondary" style={{ fontSize: 11 }}>Share of calls</Text>
                        <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: "#f5f5f5", marginTop: 4 }}>
                            {buckets.map(b => {
                                const pct = totalCalls > 0 ? (b.calls / totalCalls) * 100 : 0;
                                if (pct === 0) return null;
                                return (
                                    <Tooltip key={b.key} title={`${b.label}: ${b.calls} calls (${pct.toFixed(1)}%)`}>
                                        <div style={{ width: `${pct}%`, background: BUCKET_COLORS[b.key] ?? "#1677ff" }} />
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>
                    <div className="mb-2">
                        <Text type="secondary" style={{ fontSize: 11 }}>Share of actual talk time</Text>
                        <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: "#f5f5f5", marginTop: 4 }}>
                            {buckets.map(b => {
                                const pct = totalSeconds > 0 ? (b.total_seconds / totalSeconds) * 100 : 0;
                                if (pct === 0) return null;
                                return (
                                    <Tooltip key={b.key} title={`${b.label}: ${b.total_minutes.toFixed(1)} min (${pct.toFixed(1)}%)`}>
                                        <div style={{ width: `${pct}%`, background: BUCKET_COLORS[b.key] ?? "#1677ff" }} />
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>
                    <div className="mb-4">
                        <Text type="secondary" style={{ fontSize: 11 }}>Share of billed minutes</Text>
                        <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: "#f5f5f5", marginTop: 4 }}>
                            {buckets.map(b => {
                                const pct = totalBilled > 0 ? (b.billed_minutes / totalBilled) * 100 : 0;
                                if (pct === 0) return null;
                                return (
                                    <Tooltip key={b.key} title={`${b.label}: ${b.billed_minutes} billed min (${pct.toFixed(1)}%)`}>
                                        <div style={{ width: `${pct}%`, background: BUCKET_COLORS[b.key] ?? "#1677ff" }} />
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>

                    <Table
                        dataSource={buckets}
                        columns={columns}
                        rowKey="key"
                        size="small"
                        pagination={false}
                        scroll={{ x: 900 }}
                        summary={() => (
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0}><b>Total</b></Table.Summary.Cell>
                                <Table.Summary.Cell index={1}><b>{totalCalls}</b></Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>—</Table.Summary.Cell>
                                <Table.Summary.Cell index={3}>
                                    <b>{totalMinutes.toFixed(1)}</b> <Text type="secondary" style={{ fontSize: 11 }}>min</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4}>
                                    <b style={{ color: "#1677ff" }}>{totalBilled}</b> <Text type="secondary" style={{ fontSize: 11 }}>min</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5}>—</Table.Summary.Cell>
                                <Table.Summary.Cell index={6}>
                                    <Text type="secondary">{fmtHMS(Math.round(totalSeconds / totalCalls))}</Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        )}
                    />

                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 10 }}>
                        Buckets cover connected calls only — a call counts in one bucket, and the actual
                        minutes add up to the agent's total talk time ({row.total_duration}).
                        <b> Billed min</b> rounds every call up to the next whole minute on its own
                        (1 sec → 1 min, 1 min 30 sec → 2 min), so it is always the higher of the two.
                    </Text>
                </>
            )}
        </Modal>
    );
};

const AgentPerformanceTab = ({
    agentWiseReport,
    loading,
    agentProfiles = [],
    loadingProfiles,
    saharaTicketsCount,
    dateRange,
    callType
}: Props) => {
    const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
    const [csatDetailsAgent, setCsatDetailsAgent] = useState<string | null>(null);
    // CSAT is hidden by default; the toggle reveals it on each agent card and the overall strip
    const [showCsat, setShowCsat] = useState(false);
    const [durationDetailsAgent, setDurationDetailsAgent] = useState<any | null>(null);
    const [qaAnalyticsOpen, setQaAnalyticsOpen] = useState(false);
    const [expandedQA, setExpandedQA] = useState<Set<string>>(new Set());

    const toggleQA = (agentName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedQA(prev => {
            const next = new Set(prev);
            next.has(agentName) ? next.delete(agentName) : next.add(agentName);
            return next;
        });
    };

    const toggleAgent = (name: string) =>
        setSelectedAgent(prev => (prev === name ? null : name));

    const profileMap = useMemo(() => {
        const map: Record<string, AgentProfile> = {};
        agentProfiles.forEach(p => { map[p.agent.toLowerCase()] = p; });
        return map;
    }, [agentProfiles]);

    const getProfile = (agentName: string): AgentProfile | undefined =>
        profileMap[agentName.toLowerCase()];

    const agents = useMemo(() =>
        agentWiseReport
            .filter(r => r.agent !== "Unknown")
            .map(r => {
                // Records with no status ("Unknown") are treated as answered, not missed
                const answered = (r.status_breakdown?.Answered ?? 0) + (r.status_breakdown?.Unknown ?? 0);
                const notAnswered = r.status_breakdown?.NotAnswered ?? 0;
                const pct = r.total_calls > 0 ? Math.round((answered / r.total_calls) * 100) : 0;
                const avgDurSec = answered > 0 ? Math.round(r.total_duration_seconds / answered) : 0;
                const avgDurMin = `${Math.floor(avgDurSec / 60)}m ${avgDurSec % 60}s`;
                return { ...r, answered, notAnswered, pct, avgDurMin };
            })
            .sort((a, b) => b.total_calls - a.total_calls)
            .map((r, i) => ({ ...r, color: AGENT_COLORS[i % AGENT_COLORS.length] })),
        [agentWiseReport]
    );

    // @@ AI-COMMENT: Aggregate totals across all human agents for the KPI strip
    const totals = useMemo(() => ({
        calls: agents.reduce((s, a) => s + a.total_calls, 0),
        answered: agents.reduce((s, a) => s + a.answered, 0),
        missed: agents.reduce((s, a) => s + a.notAnswered, 0),
        durationSec: agents.reduce((s, a) => s + a.total_duration_seconds, 0),
    }), [agents]);

    // @@ AI-COMMENT: Format total duration as H:MM string
    const totalDurFormatted = useMemo(() => {
        const h = Math.floor(totals.durationSec / 3600);
        const m = Math.floor((totals.durationSec % 3600) / 60);
        return `${h}h ${m}m`;
    }, [totals.durationSec]);

    // @@ AI-COMMENT: Donut pie data for overall answered vs missed split
    const pieData = [
        { name: "Answered", value: totals.answered, fill: "#52c41a" },
        { name: "Missed", value: totals.missed, fill: "#ff4d4f" },
    ];

    // @@ AI-COMMENT: Bar chart dataset with shortened names for axis labels
    const barData = agents.map(a => ({
        name: a.agent.length > 8 ? a.agent.split(" ")[0] : a.agent,
        fullName: a.agent,
        "Total Calls": a.total_calls,
        Answered: a.answered,
        Missed: a.notAnswered,
    }));

    // @@ AI-COMMENT: Derive today's bucket label in the same format the API uses (e.g. "24 Mar")
    const todayBucket = useMemo(() => moment().format("D MMM"), []);

    // @@ AI-COMMENT: Derive chart label from the bucket_interval field of the first agent with a time_series
    const intervalLabel = useMemo(() => {
        const first = agentWiseReport.find(r => r.bucket_interval);
        return first?.bucket_interval ?? "interval";
    }, [agentWiseReport]);

    // @@ AI-COMMENT: Build combined time-series dataset preserving API bucket order, merging all agents per bucket
    const combinedTimeSeries = useMemo(() => {
        // @@ AI-COMMENT: Use the first available agent's time_series as the canonical bucket order
        const referenceAgent = agents.find(a => (a.time_series || []).length > 0);
        if (!referenceAgent?.time_series) return [];

        // @@ AI-COMMENT: Collect call counts keyed by bucket for every agent
        const allBuckets: Record<string, Record<string, number>> = {};
        agents.forEach(agent => {
            (agent.time_series || []).forEach(tb => {
                if (!allBuckets[tb.bucket]) allBuckets[tb.bucket] = {};
                allBuckets[tb.bucket][agent.agent] = tb.calls;
            });
        });

        // @@ AI-COMMENT: Map reference buckets to chart rows, skip fully-empty buckets
        return referenceAgent.time_series
            .filter(tb => {
                const row = allBuckets[tb.bucket] || {};
                return Object.values(row).some(v => v > 0);
            })
            .map(tb => ({
                bucket: shortBucket(tb.bucket),
                ...(allBuckets[tb.bucket] || {}),
            }));
    }, [agents]);

    if (!loading && agentWiseReport.length === 0) {
        return (
            <Empty
                description="No agent data for the selected period"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: 60 }}
            />
        );
    }

    return (
        <div className="space-y-5">
            {/* @@ AI-COMMENT: KPI summary statistics strip */}
            <Row gutter={[16, 16]}>
                <Col flex="1" style={{ minWidth: 140 }}>
                    <Card bordered={false} className="shadow-sm rounded-xl text-center" bodyStyle={{ padding: "16px 12px" }}>
                        <PhoneOutlined style={{ fontSize: 22, color: "#1677ff", marginBottom: 6, display: "block" }} />
                        <Statistic
                            title={<span style={{ fontSize: 11 }}>Total Calls</span>}
                            value={totals.calls}
                            valueStyle={{ fontSize: 22, color: "#1677ff" }}
                        />
                    </Card>
                </Col>
                <Col flex="1" style={{ minWidth: 140 }}>
                    <Card bordered={false} className="shadow-sm rounded-xl text-center" bodyStyle={{ padding: "16px 12px" }}>
                        <CheckCircleOutlined style={{ fontSize: 22, color: "#52c41a", marginBottom: 6, display: "block" }} />
                        <Statistic
                            title={<span style={{ fontSize: 11 }}>Answered</span>}
                            value={totals.answered}
                            valueStyle={{ fontSize: 22, color: "#52c41a" }}
                        />
                    </Card>
                </Col>
                <Col flex="1" style={{ minWidth: 140 }}>
                    <Card bordered={false} className="shadow-sm rounded-xl text-center" bodyStyle={{ padding: "16px 12px" }}>
                        <CloseCircleOutlined style={{ fontSize: 22, color: "#ff4d4f", marginBottom: 6, display: "block" }} />
                        <Statistic
                            title={<span style={{ fontSize: 11 }}>Missed</span>}
                            value={totals.missed}
                            valueStyle={{ fontSize: 22, color: "#ff4d4f" }}
                        />
                    </Card>
                </Col>
                <Col flex="1" style={{ minWidth: 140 }}>
                    <Card bordered={false} className="shadow-sm rounded-xl text-center" bodyStyle={{ padding: "16px 12px" }}>
                        <ClockCircleOutlined style={{ fontSize: 22, color: "#fa8c16", marginBottom: 6, display: "block" }} />
                        <Statistic
                            title={<span style={{ fontSize: 11 }}>Total Handle Time</span>}
                            value={totalDurFormatted}
                            valueStyle={{ fontSize: 22, color: "#fa8c16" }}
                        />
                    </Card>
                </Col>
                <Col flex="1" style={{ minWidth: 140 }}>
                    <Card bordered={false} className="shadow-sm rounded-xl text-center" bodyStyle={{ padding: "16px 12px" }}>
                        <RobotOutlined style={{ fontSize: 22, color: "#7c3aed", marginBottom: 6, display: "block" }} />
                        <Statistic
                            title={<span style={{ fontSize: 11 }}>Sahara Tickets</span>}
                            value={saharaTicketsCount ?? '-'}
                            valueStyle={{ fontSize: 22, color: "#7c3aed" }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* @@ AI-COMMENT: Bar chart and donut pie chart side by side */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                    <Card
                        title={<span style={{ fontSize: 13 }}>Calls Per Agent</span>}
                        bordered={false}
                        className="shadow-sm rounded-xl"
                        bodyStyle={{ padding: "12px 16px" }}
                    >
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={barData} barCategoryGap="30%" barGap={3} margin={{ top: 20, right: 8, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <RTooltip
                                    formatter={(val: any, name: any) => [val, name]}
                                    labelFormatter={(l: any, p: any) => p?.[0]?.payload?.fullName || l}
                                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                                />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                {/* @@ AI-COMMENT: Total Calls bars — fixed blue, Answered green, Missed red */}
                                <Bar dataKey="Total Calls" fill="#1677ff" label={<BarLabel />} radius={[4, 4, 0, 0]} fillOpacity={0.85} />
                                <Bar dataKey="Answered" fill="#52c41a" radius={[4, 4, 0, 0]} fillOpacity={0.85} />
                                <Bar dataKey="Missed" fill="#ff4d4f" radius={[4, 4, 0, 0]} fillOpacity={0.85} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card
                        title={<span style={{ fontSize: 13 }}>Answer Rate Overview</span>}
                        bordered={false}
                        className="shadow-sm rounded-xl"
                        bodyStyle={{ padding: "12px 16px" }}
                    >
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={80}
                                    paddingAngle={4}
                                    dataKey="value"
                                    labelLine={false}
                                >
                                    {pieData.map((entry, i) => (
                                        <Cell key={i} fill={entry.fill} />
                                    ))}
                                </Pie>
                                <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                            </PieChart>
                        </ResponsiveContainer>
                        {/* @@ AI-COMMENT: Custom legend below donut — no overlap, shows count + pct */}
                        <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 8 }}>
                            {pieData.map((entry) => {
                                const total = totals.answered + totals.missed;
                                const pct = total > 0 ? ((entry.value / total) * 100).toFixed(0) : "0";
                                return (
                                    <div key={entry.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: entry.fill, display: "inline-block", flexShrink: 0 }} />
                                        <span style={{ fontSize: 12, color: "#595959" }}>{entry.name}</span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: entry.fill }}>{entry.value}</span>
                                        <span style={{ fontSize: 11, color: "#8c8c8c" }}>({pct}%)</span>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* @@ AI-COMMENT: Combined time-series call volume chart across all agents, title reflects bucket_interval */}
            {combinedTimeSeries.length > 0 && (
                <Card
                    title={<span style={{ fontSize: 13 }}>Call Volume by Agent ({intervalLabel} buckets)</span>}
                    bordered={false}
                    className="shadow-sm rounded-xl"
                    bodyStyle={{ padding: "12px 16px" }}
                >
                    <ResponsiveContainer width="100%" height={320}>
                        <AreaChart data={combinedTimeSeries} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                            <defs>
                                {/* @@ AI-COMMENT: Gradient fills for each agent area series */}
                                {agents.map((a, i) => (
                                    <linearGradient key={a.agent} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={a.color} stopOpacity={0.25} />
                                        <stop offset="95%" stopColor={a.color} stopOpacity={0.04} />
                                    </linearGradient>
                                ))}
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="bucket" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={40} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                            {/* @@ AI-COMMENT: Clickable custom legend — clicking a pill isolates that agent in the chart */}
                            <Legend
                                content={() => (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 8 }}>
                                        {selectedAgent && (
                                            <button
                                                onClick={() => setSelectedAgent(null)}
                                                style={{ padding: "2px 10px", borderRadius: 9999, fontSize: 11, cursor: "pointer", border: "1.5px solid #7c3aed", background: "#ede9fe", color: "#7c3aed", fontWeight: 600 }}
                                            >
                                                ✕ Show all
                                            </button>
                                        )}
                                        {agents.map(a => {
                                            const isActive = selectedAgent === null || selectedAgent === a.agent;
                                            return (
                                                <button
                                                    key={a.agent}
                                                    onClick={() => toggleAgent(a.agent)}
                                                    style={{
                                                        display: "flex", alignItems: "center", gap: 5,
                                                        padding: "2px 10px", borderRadius: 9999, fontSize: 11,
                                                        cursor: "pointer", transition: "all 0.2s",
                                                        border: `1.5px solid ${selectedAgent === a.agent ? a.color : "#e5e7eb"}`,
                                                        background: selectedAgent === a.agent ? a.color : isActive ? "#fff" : "#f5f5f5",
                                                        color: selectedAgent === a.agent ? "#fff" : isActive ? "#333" : "#bbb",
                                                        fontWeight: selectedAgent === a.agent ? 600 : 400,
                                                    }}
                                                >
                                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: a.color, display: "inline-block", flexShrink: 0 }} />
                                                    {a.agent}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            />
                            {/* @@ AI-COMMENT: Vertical reference line marking today's bucket if present in the data */}
                            {combinedTimeSeries.some(r => r.bucket === todayBucket) && (
                                <ReferenceLine
                                    x={todayBucket}
                                    stroke="#7c3aed"
                                    strokeDasharray="4 3"
                                    strokeWidth={2}
                                    label={{ value: "Today", position: "top", fill: "#7c3aed", fontSize: 10, fontWeight: 600 }}
                                />
                            )}
                            {/* @@ AI-COMMENT: Each area fades to near-invisible when another agent is selected */}
                            {agents.map((a, i) => {
                                const isVisible = selectedAgent === null || selectedAgent === a.agent;
                                return (
                                    <Area
                                        key={a.agent}
                                        type="monotone"
                                        dataKey={a.agent}
                                        stroke={a.color}
                                        fill={`url(#grad-${i})`}
                                        strokeWidth={isVisible ? (selectedAgent === a.agent ? 3 : 2) : 1}
                                        strokeOpacity={isVisible ? 1 : 0.08}
                                        fillOpacity={isVisible ? 1 : 0.03}
                                        dot={false}
                                        activeDot={isVisible ? { r: 5 } : false}
                                        style={{ transition: "stroke-opacity 0.3s, fill-opacity 0.3s" }}
                                    />
                                );
                            })}
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>
            )}

            {(() => {
                let totalSatisfied = 0, totalNotSatisfied = 0, totalNoInput = 0;
                let weightedScore = 0, totalResponses = 0;
                agents.forEach(agent => {
                    if (agent.csat_distribution) {
                        totalSatisfied += agent.csat_distribution.Satisfied ?? 0;
                        totalNotSatisfied += agent.csat_distribution.Not_Satisfied ?? 0;
                        totalNoInput += agent.csat_distribution.No_input ?? 0;
                    }
                    const n = agent.csat_responses ?? 0;
                    if (n > 0 && typeof agent.csat_out_of_5 === "number") {
                        weightedScore += agent.csat_out_of_5 * n;
                        totalResponses += n;
                    }
                });
                const overallScore = totalResponses > 0 ? weightedScore / totalResponses : null;
                return (
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                        <div className="flex items-center gap-2">
                            <Switch size="small" checked={showCsat} onChange={setShowCsat} />
                            <Text type="secondary" style={{ fontSize: 13 }}>Show CSAT</Text>
                        </div>
                        {showCsat && overallScore !== null && (
                            <div className="flex items-center gap-2 text-lg font-semibold text-yellow-600">
                                <span style={{ fontSize: 22 }}>⭐</span>
                                Overall CSAT: {overallScore.toFixed(2)} / 5
                                <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                    ({totalResponses} {totalResponses === 1 ? "response" : "responses"})
                                </Text>
                            </div>
                        )}
                        {showCsat && (
                            <div className="flex gap-4 text-base text-gray-700 font-medium">
                                <span>Satisfied: <b>{totalSatisfied}</b></span>
                                <span>Not Satisfied: <b>{totalNotSatisfied}</b></span>
                                <span>No Input: <b>{totalNoInput}</b></span>
                            </div>
                        )}
                    </div>
                );
            })()}

            <CsatDetailsModal
                open={csatDetailsAgent !== null}
                agent={csatDetailsAgent}
                onClose={() => setCsatDetailsAgent(null)}
                defaultRange={dateRange}
            />

            <CallDurationModal
                open={durationDetailsAgent !== null}
                agent={durationDetailsAgent}
                onClose={() => setDurationDetailsAgent(null)}
                defaultRange={dateRange}
                callType={callType}
            />

            {/* @@ AI-COMMENT: Per-agent detail cards in a responsive grid */}
            <Row gutter={[16, 16]}>
                {agents.map((agent) => {
                    // @@ AI-COMMENT: Card is highlighted when it is the selected agent in the chart
                    const isSelected = selectedAgent === agent.agent;
                    // Extract CSAT fields if present
                    const csatScore = agent.csat_out_of_5;
                    const csatDist = agent.csat_distribution || {};
                    const fbDist = agent.feedback_distribution || {};
                    const fbTotal = (fbDist.Good ?? 0) + (fbDist.Average ?? 0) + (fbDist.Poor ?? 0);
                    const csatResponses = agent.csat_responses ?? 0;
                    return (
                        <Col xs={24} sm={12} md={8} lg={6} key={agent.agent}>
                            <Card
                                bordered={false}
                                className="shadow-sm rounded-xl h-full"
                                bodyStyle={{ padding: "16px" }}
                                onClick={() => toggleAgent(agent.agent)}
                                style={{
                                    cursor: "pointer",
                                    border: isSelected ? `2px solid ${agent.color}` : "1px solid transparent",
                                    boxShadow: isSelected ? `0 0 0 3px ${agent.color}22` : undefined,
                                    transition: "border 0.2s, box-shadow 0.2s",
                                }}
                            >
                                {/* @@ AI-COMMENT: Agent avatar and name header */}
                                <div className="flex items-center gap-3 mb-3">
                                    <Avatar
                                        size={36}
                                        style={{ backgroundColor: agent.color, fontSize: 14, flexShrink: 0 }}
                                    >
                                        {agent.agent.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <Text strong style={{ fontSize: 13, display: "block" }} ellipsis={{ tooltip: agent.agent }}>
                                            {agent.agent}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 11 }}>{agent.total_calls} calls</Text>
                                    </div>
                                    {/* @@ AI-COMMENT: Menu of per-agent deep dives — CSAT only
                                        shows up when this agent actually collected ratings */}
                                    <Dropdown
                                        trigger={["click"]}
                                        placement="bottomRight"
                                        menu={{
                                            items: [
                                                ...(showCsat && fbTotal > 0
                                                    ? [{
                                                        key: "csat",
                                                        icon: <StarOutlined />,
                                                        label: "Detailed CSAT",
                                                    }]
                                                    : []),
                                                {
                                                    key: "duration",
                                                    icon: <FieldTimeOutlined />,
                                                    label: "Call Duration Breakdown",
                                                },
                                            ],
                                            onClick: ({ key, domEvent }) => {
                                                domEvent.stopPropagation();
                                                if (key === "csat") setCsatDetailsAgent(agent.agent);
                                                if (key === "duration") setDurationDetailsAgent(agent);
                                            },
                                        }}
                                    >
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<MoreOutlined style={{ fontSize: 18 }} />}
                                            aria-label={`More details for ${agent.agent}`}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    </Dropdown>
                                </div>

                                {showCsat && typeof csatScore === "number" && (
                                    <div className="mt-2">
                                        <div className="flex items-baseline gap-2 mb-1">
                                            <span className="text-yellow-500 font-bold">
                                                ⭐ {csatScore.toFixed(2)} / 5
                                            </span>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {csatResponses} {csatResponses === 1 ? "response" : "responses"}
                                            </Text>
                                        </div>
                                        {fbTotal > 0 ? (
                                            <div className="flex gap-3 text-xs text-gray-600">
                                                <span>Good: <b>{fbDist.Good ?? 0}</b></span>
                                                <span>Average: <b>{fbDist.Average ?? 0}</b></span>
                                                <span>Poor: <b>{fbDist.Poor ?? 0}</b></span>
                                            </div>
                                        ) : (
                                            <div className="flex gap-3 text-xs text-gray-600">
                                                <span>Satisfied: <b>{csatDist.Satisfied ?? 0}</b></span>
                                                <span>Not Satisfied: <b>{csatDist.Not_Satisfied ?? 0}</b></span>
                                                <span>No Input: <b>{csatDist.No_input ?? 0}</b></span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* @@ AI-COMMENT: Answer rate progress bar with dynamic color */}
                                <Progress
                                    percent={agent.pct}
                                    strokeColor={rateColor(agent.pct)}
                                    trailColor="#f5f5f5"
                                    strokeWidth={7}
                                    size="small"
                                    format={pct => (
                                        <span style={{ fontSize: 11, color: rateColor(agent.pct) }}>{pct ?? 0}%</span>
                                    )}
                                />

                                {/* @@ AI-COMMENT: Answered / Missed / Avg duration stat row */}
                                <div className="flex justify-between mt-3" style={{ fontSize: 11 }}>
                                    <div className="flex flex-col items-center">
                                        <Tag color="success" style={{ margin: 0, fontSize: 11 }}>{agent.answered}</Tag>
                                        <Text type="secondary" style={{ fontSize: 10, marginTop: 2 }}>Answered</Text>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <Tag color="error" style={{ margin: 0, fontSize: 11 }}>{agent.notAnswered}</Tag>
                                        <Text type="secondary" style={{ fontSize: 10, marginTop: 2 }}>Missed</Text>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <Tag color="warning" style={{ margin: 0, fontSize: 11 }}>{agent.avgDurMin}</Tag>
                                        <Text type="secondary" style={{ fontSize: 10, marginTop: 2 }}>Avg Duration</Text>
                                    </div>
                                </div>


                                <div className="mt-2 pt-2 border-t border-gray-50 flex items-center gap-1">
                                    <ClockCircleOutlined style={{ color: "#8c8c8c", fontSize: 11 }} />
                                    <Text type="secondary" style={{ fontSize: 11 }}>Total: {agent.total_duration}</Text>
                                </div>

                                {/* QA Profile Section — collapsed by default */}
                                {(() => {
                                    const profile = getProfile(agent.agent);
                                    const isQAOpen = expandedQA.has(agent.agent);

                                    if (loadingProfiles) {
                                        return (
                                            <div className="mt-3 pt-2 border-t border-gray-100 flex justify-center">
                                                <Spin size="small" />
                                            </div>
                                        );
                                    }
                                    if (!profile) return null;

                                    return (
                                        <div className="mt-3 pt-2 border-t border-gray-100">
                                            {/* Toggle row */}
                                            <div
                                                onClick={(e) => toggleQA(agent.agent, e)}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    cursor: "pointer",
                                                    userSelect: "none",
                                                    padding: "5px 10px",
                                                    borderRadius: 7,
                                                    background: "#fafafa",
                                                    border: "1px solid #e5e7eb",
                                                    transition: "background 0.2s",
                                                }}
                                            >
                                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                    <TrophyOutlined style={{ color: "#8c8c8c", fontSize: 11 }} />
                                                    <Text style={{ fontSize: 11, color: "#595959" }}>
                                                        {isQAOpen ? "Hide QA Details" : "View QA Details"}
                                                    </Text>
                                                </div>
                                                {isQAOpen
                                                    ? <DownOutlined style={{ fontSize: 10, color: "#8c8c8c" }} />
                                                    : <RightOutlined style={{ fontSize: 10, color: "#8c8c8c" }} />
                                                }
                                            </div>

                                            {/* Expanded detail */}
                                            {isQAOpen && (
                                                <div style={{ marginTop: 8 }}>
                                                    {/* Dimension breakdown */}
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                        {Object.entries(profile.dimension_scores).map(([key, dim]) => {
                                                            const label = DIMENSION_LABELS[key] ?? key;
                                                            const hasScore = dim.score !== null;
                                                            const pct = hasScore ? Math.min(100, Math.round((dim.score! / 100) * 100)) : 0;
                                                            return (
                                                                <div key={key}>
                                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                                                                        <Text style={{ fontSize: 10, color: "#595959" }}>{label}</Text>
                                                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                                            <Text style={{ fontSize: 10, color: "#8c8c8c" }}>{dim.weight}</Text>
                                                                            {hasScore
                                                                                ? <Text style={{ fontSize: 10, fontWeight: 600, color: pct >= 60 ? "#389e0d" : pct >= 40 ? "#d48806" : "#cf1322" }}>{dim.score!.toFixed(1)}</Text>
                                                                                : <Tooltip title={`${dim.calls_skipped} calls skipped`}><Text style={{ fontSize: 10, color: "#bfbfbf" }}>N/A <InfoCircleOutlined style={{ fontSize: 9 }} /></Text></Tooltip>
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                    <Progress
                                                                        percent={pct}
                                                                        showInfo={false}
                                                                        strokeWidth={4}
                                                                        strokeColor={hasScore ? (pct >= 60 ? "#52c41a" : pct >= 40 ? "#faad14" : "#ff4d4f") : "#f0f0f0"}
                                                                        trailColor="#f5f5f5"
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>


                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* QA Analytics charts section — collapsed by default */}
            {agentProfiles.length > 0 && (
                <div>
                    <div
                        onClick={() => setQaAnalyticsOpen(prev => !prev)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            cursor: "pointer",
                            userSelect: "none",
                            padding: "10px 16px",
                            borderRadius: 10,
                            background: qaAnalyticsOpen ? "#ede9fe" : "#f5f5f5",
                            border: `1.5px solid ${qaAnalyticsOpen ? "#7c3aed" : "#e5e7eb"}`,
                            transition: "background 0.2s, border-color 0.2s",
                            marginBottom: qaAnalyticsOpen ? 12 : 0,
                        }}
                    >
                        {qaAnalyticsOpen
                            ? <DownOutlined style={{ fontSize: 13, color: "#7c3aed" }} />
                            : <RightOutlined style={{ fontSize: 13, color: "#595959" }} />
                        }
                        <span style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: qaAnalyticsOpen ? "#7c3aed" : "#262626",
                        }}>
                            QA Performance Analytics
                        </span>
                        <span style={{ marginLeft: "auto", fontSize: 11, color: "#8c8c8c" }}>
                            {qaAnalyticsOpen ? "Click to hide" : "Click to view"}
                        </span>
                    </div>
                    {qaAnalyticsOpen && <AgentProfileCharts agentProfiles={agentProfiles} />}
                </div>
            )}
        </div>
    );
};

export default AgentPerformanceTab;
